import { useCallback, useMemo } from "react";
import {
  Calendar,
  Views,
  dateFnsLocalizer,
  type EventPropGetter,
} from "react-big-calendar";
import withDragAndDropModule from "react-big-calendar/lib/addons/dragAndDrop";
import type { EventInteractionArgs } from "react-big-calendar/lib/addons/dragAndDrop";

const withDragAndDrop = (
  "default" in withDragAndDropModule
    ? withDragAndDropModule.default
    : withDragAndDropModule
) as typeof withDragAndDropModule;
import {
  format,
  parse,
  startOfWeek,
  getDay,
  setHours,
  setMinutes,
} from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import type {
  SchedulerEvent,
  SchedulerProps,
  SchedulerResource,
  SchedulerSlotInfo,
} from "./types";
import "./scheduler.css";

const locales = {
  "en-US": enUS,
};

const defaultLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const DragAndDropCalendar = withDragAndDrop<SchedulerEvent, SchedulerResource>(
  Calendar,
);

const defaultViews = [
  Views.DAY,
  Views.WORK_WEEK,
  Views.WEEK,
  Views.MONTH,
  Views.AGENDA,
];

const defaultMinTime = setHours(setMinutes(new Date(), 0), 6);
const defaultMaxTime = setHours(setMinutes(new Date(), 0), 22);

export function Scheduler({
  events,
  resources,
  onEventCreate,
  onEventUpdate,
  onEventSelect,
  onRangeChange,
  onViewChange,
  date,
  defaultDate,
  defaultView = Views.WEEK,
  height = "720px",
  selectable = true,
  showToolbar = true,
  localizer,
}: SchedulerProps) {
  const schedulerLocalizer = useMemo(
    () => localizer ?? defaultLocalizer,
    [localizer],
  );

  const resourceColorMap = useMemo(() => {
    if (!resources) {
      return new Map<string, string>();
    }
    return new Map<string, string>(
      resources
        .filter((resource) => Boolean(resource.color))
        .map((resource) => [resource.id, resource.color as string]),
    );
  }, [resources]);

  const calendarProps = useMemo(() => {
    const props: Record<string, unknown> = {
      selectable,
    };
    if (resources && resources.length > 0) {
      props.resources = resources;
      props.resourceIdAccessor = "id";
      props.resourceTitleAccessor = "title";
      props.resourcePropGetter = (resource: SchedulerResource) => {
        const color = resourceColorMap.get(resource.id);
        if (!color) {
          return {};
        }
        return {
          style: {
            borderLeft: `4px solid ${color}`,
            paddingLeft: "0.75rem",
          },
        };
      };
    }
    return props;
  }, [selectable, resources, resourceColorMap]);

  const handleEventDrop = useCallback(
    ({
      event,
      start,
      end,
      resourceId,
    }: EventInteractionArgs<SchedulerEvent>) => {
      if (!onEventUpdate) {
        return;
      }
      onEventUpdate({
        ...event,
        start: new Date(start),
        end: new Date(end),
        resourceId: resourceId ? String(resourceId) : event.resourceId,
      });
    },
    [onEventUpdate],
  );

  const handleEventResize = useCallback(
    ({
      event,
      start,
      end,
      resourceId,
    }: EventInteractionArgs<SchedulerEvent>) => {
      if (!onEventUpdate) {
        return;
      }
      onEventUpdate({
        ...event,
        start: new Date(start),
        end: new Date(end),
        resourceId: resourceId ? String(resourceId) : event.resourceId,
      });
    },
    [onEventUpdate],
  );

  const handleSelectSlot = useCallback(
    (slotInfo: SchedulerSlotInfo) => {
      if (!onEventCreate) {
        return;
      }
      onEventCreate({
        start: slotInfo.start,
        end: slotInfo.end,
        resourceId: slotInfo.resourceId
          ? String(slotInfo.resourceId)
          : undefined,
      });
    },
    [onEventCreate],
  );

  const eventPropGetter = useCallback<EventPropGetter<SchedulerEvent>>(
    (event) => {
      const backgroundColor =
        event.color ??
        (event.resourceId
          ? resourceColorMap.get(event.resourceId)
          : undefined) ??
        "var(--scheduler-event-color)";
      return {
        style: {
          backgroundColor,
          borderRadius: "8px",
          border: "none",
          color: "#fff",
          boxShadow: "0 6px 16px -12px rgba(22, 101, 216, 0.8)",
        },
      };
    },
    [resourceColorMap],
  );

  return (
    <div className="scheduler-wrapper" style={{ height }}>
      <DragAndDropCalendar
        {...calendarProps}
        localizer={schedulerLocalizer}
        events={events}
        defaultView={defaultView}
        views={defaultViews}
        defaultDate={defaultDate ?? new Date()}
        date={date}
        style={{ height: "100%" }}
        popup
        showMultiDayTimes
        step={30}
        timeslots={2}
        toolbar={showToolbar}
        selectable={selectable}
        resizable={Boolean(onEventUpdate)}
        onSelectSlot={handleSelectSlot}
        onRangeChange={onRangeChange}
        onEventDrop={handleEventDrop}
        onEventResize={handleEventResize}
        onSelectEvent={onEventSelect}
        onView={onViewChange}
        eventPropGetter={eventPropGetter}
        draggableAccessor={() => Boolean(onEventUpdate)}
        min={defaultMinTime}
        max={defaultMaxTime}
      />
    </div>
  );
}
